/**
 * The evening review on Home: the assistant's summary of the day, every entry
 * with a checkbox, the brief's suggestion chips, and an instruction box that
 * is a real assistant turn at practice focus (its route names the day, so the
 * day's entries are in the reminder). Only the timekeeper sees it.
 */
import {
  ArrowUp,
  Check,
  CircleCheck,
  Lock,
  PanelRight,
  Sparkles
} from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { agentPanelStore } from "@/features/agents/agentPanelStore";
import { AgentTranscript } from "@/features/agents/components/AgentTranscript";
import { AssistantAvatar } from "@/features/agents/components/AssistantAvatar";
import type { AgentScope } from "@/features/agents/types";
import type { SerializedSelection } from "@/features/agents/useAgentChat";
import { useScopedAgentChat } from "@/features/agents/useScopedAgentChat";
import { cn } from "@/lib/utils";

import { timeActions } from "../actions";
import { useTimeBrief } from "../queries";
import type { BriefSuggestion } from "../types";
import { formatHours, totalHours } from "../utils";
import {
  applySuggestion,
  isOpen,
  readyToRelease,
  REVIEW_PARAM,
  reviewTimeLabel,
  suggestionKey
} from "./homeModel";
import { ReviewRow } from "./ReviewRow";

export const TIME_REVIEW_OWNER = "time-review";

type Props = {
  date: string;
  practice: AgentScope;
  onOpenTimesheet: (date: string) => void;
};

export const EveningReviewCard: FC<Props> = ({
  date,
  practice,
  onOpenTimesheet
}) => {
  const brief = useTimeBrief(date);
  const [unchecked, setUnchecked] = useState<Set<string>>(() => new Set());
  const [used, setUsed] = useState<Set<string>>(() => new Set());
  const [applied, setApplied] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const selection = useCallback(
    (): SerializedSelection => ({
      artifact: null,
      text: null,
      route: `/home?${REVIEW_PARAM}=${date}`
    }),
    [date]
  );
  const scoped = useScopedAgentChat({
    agent: "practice",
    scope: practice,
    spec: null,
    owner: TIME_REVIEW_OWNER,
    threadKey: `time-review:${practice.id}:${date}`,
    adapters: { selection }
  });
  const { chat } = scoped;

  const { data } = brief;
  const entries = useMemo(() => data?.entries ?? [], [data]);
  const open = entries.filter(isOpen);
  const ready = readyToRelease(entries, unchecked);
  const chips = (data?.suggestions ?? []).filter(
    (s) => !used.has(suggestionKey(s))
  );
  const talking = chat.messages.length > 0 || scoped.streaming;

  if (brief.isLoading) {
    return (
      <Skeleton
        className="h-55 w-full rounded-xl"
        data-testid="evening-review-loading"
      />
    );
  }
  if (!data || !entries.length) return null;

  if (!open.length && !talking) {
    return (
      <div
        className="flex items-center gap-2.5 rounded-xl border border-line-2 p-4"
        data-testid="evening-review-done"
      >
        <CircleCheck className="size-4.5 text-success" />
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">
            {data.weekday} is released
          </span>
          <span className="text-sm text-fg-3 tabular-nums">
            {formatHours(data.totals.hours)} went to Billing review.
          </span>
        </span>
      </div>
    );
  }

  const toggle = (id: string) =>
    setUnchecked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runChip = async (s: BriefSuggestion) => {
    const key = suggestionKey(s);
    setBusy(key);
    const ok = await applySuggestion(s);
    setBusy(null);
    if (!ok) return;
    setUsed((cur) => new Set(cur).add(key));
    setApplied((cur) => [...cur, s.label]);
  };

  const sendDraft = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    scoped.send(text);
  };

  return (
    <div
      className="flex shrink-0 flex-col overflow-hidden rounded-xl border border-line-2"
      data-testid="evening-review"
    >
      <div className="flex flex-col gap-2.5 px-4 pt-3.5 pb-3">
        <span className="flex items-center gap-2">
          <AssistantAvatar
            agent="practice"
            seed={`practice:${practice.id}`}
            size="sm"
          />
          <span className="text-base font-medium">{scoped.label}</span>
          <span className="truncate text-sm text-fg-3">
            {reviewTimeLabel(data.review_time)} · Your time for {data.weekday}
          </span>
          <span className="flex-1" />
          <Badge variant="secondary" className="gap-1 font-normal">
            <Lock className="size-2.5" />
            Only you
          </Badge>
        </span>
        <p className="text-md leading-5.5" data-testid="evening-review-summary">
          {data.summary}
        </p>
      </div>
      <div className="border-t border-line-2">
        {entries.map((e, i) => (
          <ReviewRow
            key={e.id}
            entry={e}
            first={i === 0}
            selectable
            checked={!unchecked.has(e.id)}
            onToggle={() => toggle(e.id)}
          />
        ))}
      </div>
      {applied.length || data.learned.length ? (
        <div className="flex flex-col gap-1.5 border-t border-line-2 bg-fill-1 px-4 py-2.5 text-base">
          {applied.map((line, i) => (
            <span key={`a${i}`} className="flex items-start gap-2">
              <Check className="mt-0.5 size-3.5 shrink-0 text-success" />
              {line}
            </span>
          ))}
          {data.learned.map((l) => (
            <span
              key={l.id}
              className="flex items-start gap-2 text-fg-3"
              data-testid="evening-review-learned"
            >
              <Sparkles className="mt-0.5 size-3.5 shrink-0 text-primary" />
              {l.label}
            </span>
          ))}
        </div>
      ) : null}
      {talking ? (
        <div className="flex max-h-90 min-h-30 flex-col border-t border-line-2 bg-fill-1">
          <AgentTranscript scoped={scoped} contentClassName="gap-3 p-3" />
          {chat.threadId ? (
            <Button
              size="xs"
              variant="ghost"
              className="m-1 self-end text-fg-3"
              onClick={() =>
                agentPanelStore.openWith({
                  agent: "practice",
                  threadId: chat.threadId as string
                })
              }
            >
              <PanelRight />
              Continue in the assistant panel
            </Button>
          ) : null}
        </div>
      ) : null}
      {!open.length ? (
        <div
          className="flex items-center gap-2.5 border-t border-line-2 p-3"
          data-testid="evening-review-released"
        >
          <CircleCheck className="size-4 text-success" />
          <span className="flex-1 text-base tabular-nums">
            {data.weekday} is released · {formatHours(data.totals.hours)} went
            to Billing review
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenTimesheet(date)}
          >
            Open timesheet
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 border-t border-line-2 p-3">
          {chips.length ? (
            <span
              className="flex flex-wrap gap-1.5"
              data-testid="evening-review-chips"
            >
              {chips.map((s) => {
                const key = suggestionKey(s);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void runChip(s)}
                    className={cn(
                      "inline-flex h-6.5 items-center gap-1.5 rounded-full border border-line-2 bg-surface px-2.5 text-sm hover:bg-accent disabled:opacity-60",
                      busy === key && "animate-pulse"
                    )}
                  >
                    <Sparkles className="size-2.75 text-primary" />
                    {s.label}
                  </button>
                );
              })}
            </span>
          ) : null}
          <span className="flex items-center gap-2">
            <form
              className="relative flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                sendDraft();
              }}
            >
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Tell me what to change, like “the 1:00 call was 45 minutes”"
                className="h-8 pr-9 text-base"
                aria-label="Tell Casey what to change"
                data-testid="evening-review-input"
              />
              <Button
                type="submit"
                size="icon-xs"
                variant="ghost"
                className="absolute top-1/2 right-1 -translate-y-1/2"
                disabled={!draft.trim() || scoped.streaming}
                aria-label="Send"
              >
                <ArrowUp />
              </Button>
            </form>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenTimesheet(date)}
            >
              Open timesheet
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!ready.length}
              onClick={() => void timeActions.release(ready)}
              data-testid="evening-review-release"
            >
              <Check />
              Release {ready.length} · {formatHours(totalHours(ready))}
            </Button>
          </span>
        </div>
      )}
    </div>
  );
};
